import { useEffect } from "react";

import { Fullscreen } from "@material-ui/icons";

import { ToolbarButton } from "components/base";
import { useGeomBinContext } from "components/geom-bin/hooks/useGeomBinContext";

import { useMapContext } from "../hooks/useMapContext";
import { useMapDispatch } from "../hooks/useMapDispatch";

export default function MapFullscreen({ onFullscreen }) {
  const { isFullscreen, isScreenshotMode } = useMapContext();
  const mapDispatch = useMapDispatch();
  const { isActive: isGeomBinOpen } = useGeomBinContext();
  const handleToggle = () => (value) => {
    mapDispatch({ payload: { isFullscreen: value } });
  };

  // Screenshot composition owns `isFullscreen` (see `screenshotModePayload`), and this
  // effect writes it alone. A geom-bin open or close cannot happen while composing today —
  // the screenshot toggle is gated on the geom-bin being closed, and every geom-bin control
  // sits in chrome that `body.screenshot-mode` makes inert — but the guard keeps a future
  // change to either from collapsing the map under a frame that keeps its size.
  useEffect(() => {
    if (isScreenshotMode) {
      return;
    }
    mapDispatch({ payload: { isFullscreen: isGeomBinOpen } });
  }, [isGeomBinOpen, isScreenshotMode, mapDispatch]);

  useEffect(() => {
    onFullscreen && onFullscreen(isFullscreen);

    // Should not listen to onFullscreen
  }, [isFullscreen]);

  // Withheld for the same reason: toggling from here mid-composition would write
  // `isFullscreen` alone. The effects above still run so the pane keeps tracking the flag.
  // This is one of the two readers of `isScreenshotMode` (the other is the quantile freeze
  // in `MapboxWrapper`), and the visible one: a flag stranded true by an exit that skipped
  // `screenshotModePayload` shows up as this button missing for the rest of the session.
  if (isScreenshotMode) {
    return null;
  }

  return (
    // "Key" will help react to re-render the button and correct the state of tooltip
    // Without re-render, tooltip will remember the state of mouse-over -> tooltip not closed even though the window was already expanded/collapsed
    <ToolbarButton
      active={isFullscreen}
      icon={<Fullscreen style={{ fontSize: "22px" }} />}
      key={"MapFullscreen" + isFullscreen}
      onToggle={handleToggle()}
      overflowLabel={"Toggle Fullscreen"}
      styleKey="miniMapToolbar"
      testId="map-fullscreen"
      tooltipText="Toggle Fullscreen"
    />
  );
}
